// Email text handling for the Krapf charter inbox — turning a raw Outlook message into the ONLY the
// customer actually just wrote.
//
// This is a deliberate port of the already-hardened Python in
// movorra-agents/app/channels/email_channel.py (_html_to_text, _strip_quoted_and_signature,
// _is_auto_submitted, _reply_subject). That module drives the SES email channel for the agents
// service; this one drives the Graph email channel for the Studio flow. Same problem, different
// runtime, so the regexes and the reasoning are carried across rather than reinvented.
//
// Why stripping matters more here than it looks: each inbound email is ONE TURN of a flow session.
// A reply quotes the entire prior thread beneath the new text, so without stripping we would re-feed
// every earlier value to the extractor on every round — the model would keep "re-learning" stale
// details, and a correction ("actually we leave Friday") would sit in the same payload as the
// original it corrects. Cut the quote and each turn carries only new information.

// Quoted-reply markers: a reply quotes the prior thread below the new text. Cut at the first.
const ON_WROTE_RE = /^On\b.*\bwrote:\s*$/i;
const ORIG_MSG_RE = /^\s*-{2,}\s*(original message|forwarded message)\s*-{2,}\s*$/i;
// Outlook opens a quote with a "From: …" line immediately followed by Sent:/Date:/To:.
const OUTLOOK_HDR_RE = /^\s*From:\s.+/i;
const OUTLOOK_HDR_NEXT_RE = /^\s*(Sent|Date|To):\s/i;

/** Robust HTML -> text: Graph hands back HTML bodies for most real mail. */
export function htmlToText(html) {
  if (!html) return '';
  let s = String(html);
  s = s.replace(/<(script|style)\b[\s\S]*?<\/\1>/gi, ' ');
  s = s.replace(/<br\s*\/?>/gi, '\n');
  s = s.replace(/<\/(p|div|tr|li|h[1-6])>/gi, '\n');
  s = s.replace(/<[^>]+>/g, '');
  s = decodeEntities(s);
  s = s.replace(/[ \t]+\n/g, '\n');
  s = s.replace(/\n{3,}/g, '\n\n');
  return s.trim();
}

function decodeEntities(s) {
  // Named entities common in mail, then numeric. A DOM-based decode would also execute nothing, but
  // this module is pure so it can be unit-tested without jsdom.
  const named = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', '#39': "'" };
  return s
    .replace(/&([a-z]+|#\d+);/gi, (m, g) => {
      const k = g.toLowerCase();
      if (named[k] !== undefined) return named[k];
      if (k.startsWith('#')) return String.fromCharCode(Number(k.slice(1)));
      return m;
    });
}

/**
 * Return only the NEW message: cut quoted reply history (top-post `On … wrote:`, `>` quotes,
 * Outlook `-----Original Message-----` and `From:`-block dividers) and the `-- ` signature
 * delimiter. The top-post assumption (new text precedes the quote) holds for ~all mail clients.
 */
export function stripQuotedAndSignature(text) {
  if (!text) return '';
  const lines = String(text).split(/\r?\n/);
  const kept = [];
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    const stripped = line.trim();
    // RFC 3676 signature delimiter: a line that is exactly "-- " (or "--").
    if (stripped === '--' || line.replace(/\r$/, '') === '-- ') break;
    if (ON_WROTE_RE.test(stripped) || ORIG_MSG_RE.test(stripped)) break;
    if (OUTLOOK_HDR_RE.test(line) && i + 1 < lines.length && OUTLOOK_HDR_NEXT_RE.test(lines[i + 1])) break;
    if (stripped.startsWith('>')) break; // entered the quoted region (Gmail / Apple top-post)
    kept.push(line);
  }
  return kept.join('\n').trim();
}

/**
 * Mail-loop guard: true for auto-replies / out-of-office / bulk, so the inbox never starts a trip
 * intake off a vacation responder and never ping-pongs with a bot. `headers` is a plain object of
 * internet headers as Graph returns them.
 */
export function isAutoSubmitted(headers) {
  const get = (name) => {
    if (!headers) return '';
    const hit = Object.keys(headers).find((k) => k.toLowerCase() === name.toLowerCase());
    return hit ? String(headers[hit] || '').trim().toLowerCase() : '';
  };
  const autoSubmitted = get('Auto-Submitted');
  if (autoSubmitted && autoSubmitted !== 'no') return true;
  if (['auto_replied', 'auto-replied', 'bulk', 'junk'].includes(get('Precedence'))) return true;
  return ['X-Autoreply', 'X-Autorespond', 'X-Auto-Response-Suppress'].some((h) => !!get(h));
}

/** "Re: " the subject exactly once. */
export function replySubject(subject) {
  const s = String(subject || '').trim() || 'Your charter request';
  return s.slice(0, 3).toLowerCase() === 're:' ? s : `Re: ${s}`;
}

/**
 * The text of one inbound message as a flow turn.
 *
 * The first turn carries a synthetic `From:`/`Subject:` header block above the body. There is no way
 * to prefill attribute values when starting a runtime session, so this is how the sender's address
 * and the subject line reach the extractor at all — `contactEmail`'s extraction hint tells it to read
 * the From line. Later turns are body-only: the header block would be noise, and re-asserting the
 * From address every round is exactly the stale re-feeding we strip quotes to avoid.
 */
export function messageToTurnText(message, { withHeaders = false } = {}) {
  const raw = message.bodyIsHtml ? htmlToText(message.body) : String(message.body || '');
  const body = stripQuotedAndSignature(raw);
  if (!withHeaders) return body;
  const from = message.fromName
    ? `${message.fromName} <${message.fromAddress}>`
    : String(message.fromAddress || '');
  return [`From: ${from}`, `Subject: ${message.subject || ''}`, '', body].join('\n');
}

// The runtime rejects a message over 16,000 characters (RuntimeController MAX_MESSAGE_LEN) with a
// 413. A quoted-stripped charter email is nowhere near that, but a forwarded chain with an inlined
// attachment can be, and a silent 413 mid-demo is worse than a truncated turn.
export const MAX_TURN_CHARS = 16000;

export function capTurnText(text) {
  const s = String(text || '');
  return s.length <= MAX_TURN_CHARS ? s : `${s.slice(0, MAX_TURN_CHARS - 20)}\n…[truncated]`;
}
